import React, { useState, useEffect } from 'react';
import {
  BookmarkPlus,
  RotateCcw,
  Check,
  Share2,
  Gauge,
} from 'lucide-react';
import { CalcTarget, ThemeColor, DistanceUnit, PaceOrSpeedMode, Language } from '../types';
import HeroResultCard from './cards/HeroResultCard';
import DistanceInputCard from './cards/DistanceInputCard';
import TimeInputCard from './cards/TimeInputCard';
import PaceInputCard from './cards/PaceInputCard';
import { themeMap } from '../utils/calcTheme';
import { t, tf } from '../utils/i18n';
import {
  RACE_PRESETS,
  KM_PER_MILE,
  MILES_PER_KM,
  kmToMiles,
  milesToKm,
  kmPaceToMilePace,
  formatTime,
  formatPace,
  secondsToHms,
  hmsToSeconds,
  paceToMs,
  calculateTime,
  calculatePace,
  calculateSpeed,
  calculateDistance
} from '../utils/calculator';

const triggerHaptic = (duration = 10) => {
  if (typeof window !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(duration);
    } catch {
      // Ignored
    }
  }
};

interface PaceCalculatorViewProps {
  themeColor: ThemeColor;
  language?: Language;
  distanceKm: number;
  paceSecsPerKm: number;
  totalSeconds: number;
  calcTarget: CalcTarget;
  distanceUnit: DistanceUnit;
  paceOrSpeedMode: PaceOrSpeedMode;
  onChangeCalcTarget: (target: CalcTarget) => void;
  onUpdateRunnerState: (distance: number, pace: number, time: number) => void;
  onSaveToLogbook: (entry: {
    distanceKm: number;
    totalSeconds: number;
    paceSecondsPerKm: number;
    speedKmh: number;
    title?: string;
  }) => void;
  onGoToSplits: () => void;
  onOpenPerformance: () => void;
}

export default function PaceCalculatorView({
  themeColor = 'violet',
  language = 'nl',
  distanceKm: initialDistanceKm,
  paceSecsPerKm: initialPaceSecsPerKm,
  totalSeconds: initialTotalSeconds,
  calcTarget,
  distanceUnit,
  paceOrSpeedMode,
  onChangeCalcTarget,
  onUpdateRunnerState,
  onSaveToLogbook,
  onGoToSplits,
  onOpenPerformance,
}: PaceCalculatorViewProps) {
  const setCalcTarget = (target: CalcTarget) => {
    triggerHaptic(12);
    onChangeCalcTarget(target);
  };

  // Distance state (canonical is KM)
  const [distanceKm, setDistanceKm] = useState<number>(initialDistanceKm || 10);
  const [selectedPresetId, setSelectedPresetId] = useState<string>('10k');

  // Time components (hours, minutes, seconds)
  const initHms = secondsToHms(initialTotalSeconds || 3000);
  const [hours, setHours] = useState<number>(initHms.hours);
  const [minutes, setMinutes] = useState<number>(initHms.minutes);
  const [seconds, setSeconds] = useState<number>(initHms.seconds);

  // Pace components in KM
  const initMs = paceToMs(initialPaceSecsPerKm || 300);
  const [paceMinutes, setPaceMinutes] = useState<number>(initMs.minutes);
  const [paceSeconds, setPaceSeconds] = useState<number>(initMs.seconds);

  // Notifications
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  // Core values
  const totalSeconds = hmsToSeconds(hours, minutes, seconds);
  const paceSecsPerKm = paceMinutes * 60 + paceSeconds;
  const currentSpeedKmh = calculateSpeed(distanceKm, totalSeconds);

  // Unit conversions
  const distanceMiles = kmToMiles(distanceKm);
  const paceSecsPerMile = kmPaceToMilePace(paceSecsPerKm);
  const currentSpeedMph = currentSpeedKmh * MILES_PER_KM;

  // Active display values based on preferences
  const isMiles = distanceUnit === 'mi';
  const displayDistance = isMiles ? distanceMiles : distanceKm;
  const displayDistanceUnit = distanceUnit;
  const displayPaceSecs = isMiles ? paceSecsPerMile : paceSecsPerKm;
  const displaySpeed = isMiles ? currentSpeedMph : currentSpeedKmh;
  const displaySpeedUnit = isMiles ? 'mph' : 'km/u';
  const displayPaceUnit = `/${distanceUnit}`;

  // Breakdown of distance in KM
  const distWholeKm = Math.floor(distanceKm);
  const distMeters = Math.round((distanceKm - Math.floor(distanceKm)) * 1000);

  // Breakdown of distance in MILES
  const distWholeMiles = Math.floor(distanceMiles);
  const distFeet = Math.min(5279, Math.max(0, Math.round((distanceMiles - distWholeMiles) * 5280)));

  // Breakdown of pace in MILES
  const paceMileMs = paceToMs(paceSecsPerMile);

  // Sync state back to parent
  useEffect(() => {
    onUpdateRunnerState(distanceKm, paceSecsPerKm, totalSeconds);
  }, [distanceKm, paceSecsPerKm, totalSeconds]);

  // Synchronize calculations when target changes
  useEffect(() => {
    if (calcTarget === 'time') {
      const calculatedSecs = calculateTime(distanceKm, paceSecsPerKm);
      const hms = secondsToHms(calculatedSecs);
      setHours(hms.hours);
      setMinutes(hms.minutes);
      setSeconds(hms.seconds);
    } else if (calcTarget === 'pace') {
      if (distanceKm > 0 && totalSeconds > 0) {
        const calculatedPaceSecs = calculatePace(distanceKm, totalSeconds);
        const ms = paceToMs(calculatedPaceSecs);
        setPaceMinutes(ms.minutes);
        setPaceSeconds(ms.seconds);
      }
    } else if (calcTarget === 'distance') {
      if (paceSecsPerKm > 0 && totalSeconds > 0) {
        const calculatedDist = calculateDistance(totalSeconds, paceSecsPerKm);
        setDistanceKm(Math.round(calculatedDist * 1000) / 1000);
      }
    }
  }, [calcTarget]);

  // Handle manual Distance adjustments
  const handleDistanceChange = (newKm: number, presetId: string = 'custom') => {
    const validKm = Math.max(0.01, Math.min(500, newKm));
    setDistanceKm(validKm);
    setSelectedPresetId(presetId);

    if (calcTarget === 'time') {
      const calculatedSecs = calculateTime(validKm, paceSecsPerKm);
      const hms = secondsToHms(calculatedSecs);
      setHours(hms.hours);
      setMinutes(hms.minutes);
      setSeconds(hms.seconds);
    } else if (calcTarget === 'pace') {
      if (validKm > 0 && totalSeconds > 0) {
        const calculatedPaceSecs = calculatePace(validKm, totalSeconds);
        const ms = paceToMs(calculatedPaceSecs);
        setPaceMinutes(ms.minutes);
        setPaceSeconds(ms.seconds);
      }
    }
  };

  const adjustWholeKm = (delta: number) => {
    triggerHaptic(8);
    const newWhole = Math.max(0, distWholeKm + delta);
    handleDistanceChange(newWhole + distMeters / 1000);
  };

  const adjustMeters = (delta: number) => {
    triggerHaptic(8);
    let newM = distMeters + delta;
    let newWhole = distWholeKm;
    if (newM >= 1000) {
      newWhole += Math.floor(newM / 1000);
      newM = newM % 1000;
    } else if (newM < 0) {
      if (newWhole > 0) {
        newWhole -= 1;
        newM = 1000 + newM;
      } else {
        newM = 0;
      }
    }
    handleDistanceChange(newWhole + newM / 1000);
  };

  const adjustWholeMiles = (delta: number) => {
    triggerHaptic(8);
    const newWholeMiles = Math.max(0, distWholeMiles + delta);
    const newTotalMiles = newWholeMiles + distFeet / 5280;
    handleDistanceChange(milesToKm(newTotalMiles));
  };

  const adjustFeet = (delta: number) => {
    triggerHaptic(8);
    let newFeet = distFeet + delta;
    let newWholeMiles = distWholeMiles;
    if (newFeet >= 5280) {
      newWholeMiles += Math.floor(newFeet / 5280);
      newFeet = newFeet % 5280;
    } else if (newFeet < 0) {
      if (newWholeMiles > 0) {
        newWholeMiles -= 1;
        newFeet = 5280 + newFeet;
      } else {
        newFeet = 0;
      }
    }
    const newTotalMiles = newWholeMiles + newFeet / 5280;
    handleDistanceChange(milesToKm(newTotalMiles));
  };

  const setExactFeet = (feet: number) => {
    triggerHaptic(10);
    const cleanFeet = Math.max(0, Math.min(5279, feet));
    const newTotalMiles = distWholeMiles + cleanFeet / 5280;
    handleDistanceChange(milesToKm(newTotalMiles));
  };

  // Handle manual Time adjustments
  const handleTimeChange = (h: number, m: number, s: number) => {
    const validH = Math.max(0, Math.min(99, h));
    const validM = Math.max(0, Math.min(59, m));
    const validS = Math.max(0, Math.min(59, s));

    setHours(validH);
    setMinutes(validM);
    setSeconds(validS);

    const newTotalSecs = hmsToSeconds(validH, validM, validS);

    if (calcTarget === 'pace') {
      if (distanceKm > 0 && newTotalSecs > 0) {
        const calculatedPaceSecs = calculatePace(distanceKm, newTotalSecs);
        const ms = paceToMs(calculatedPaceSecs);
        setPaceMinutes(ms.minutes);
        setPaceSeconds(ms.seconds);
      }
    } else if (calcTarget === 'distance') {
      if (paceSecsPerKm > 0 && newTotalSecs > 0) {
        const calculatedDist = calculateDistance(newTotalSecs, paceSecsPerKm);
        setDistanceKm(Math.round(calculatedDist * 1000) / 1000);
      }
    }
  };

  // Handle manual Pace adjustments (canonical KM)
  const handlePaceChangeKm = (m: number, s: number) => {
    const validM = Math.max(1, Math.min(30, m));
    const validS = Math.max(0, Math.min(59, s));

    setPaceMinutes(validM);
    setPaceSeconds(validS);

    const newPaceSecs = validM * 60 + validS;

    if (calcTarget === 'time') {
      const calculatedSecs = calculateTime(distanceKm, newPaceSecs);
      const hms = secondsToHms(calculatedSecs);
      setHours(hms.hours);
      setMinutes(hms.minutes);
      setSeconds(hms.seconds);
    } else if (calcTarget === 'distance') {
      if (newPaceSecs > 0 && totalSeconds > 0) {
        const calculatedDist = calculateDistance(totalSeconds, newPaceSecs);
        setDistanceKm(Math.round(calculatedDist * 1000) / 1000);
      }
    }
  };

  // Handle manual Pace adjustments in MILES
  const handlePaceChangeMiles = (m: number, s: number) => {
    const validM = Math.max(1, Math.min(45, m));
    const validS = Math.max(0, Math.min(59, s));
    const totalSecPerMile = validM * 60 + validS;
    const totalSecPerKm = totalSecPerMile / KM_PER_MILE;

    const ms = paceToMs(totalSecPerKm);
    setPaceMinutes(ms.minutes);
    setPaceSeconds(ms.seconds);

    if (calcTarget === 'time') {
      const calculatedSecs = calculateTime(distanceKm, totalSecPerKm);
      const hms = secondsToHms(calculatedSecs);
      setHours(hms.hours);
      setMinutes(hms.minutes);
      setSeconds(hms.seconds);
    } else if (calcTarget === 'distance') {
      if (totalSecPerKm > 0 && totalSeconds > 0) {
        const calculatedDist = calculateDistance(totalSeconds, totalSecPerKm);
        setDistanceKm(Math.round(calculatedDist * 1000) / 1000);
      }
    }
  };

  // Handle manual Speed adjustments
  const handleSpeedChange = (newSpeed: number) => {
    const validSpeed = Math.max(1, Math.min(60, newSpeed));
    const speedKmh = isMiles ? validSpeed * KM_PER_MILE : validSpeed;
    const newPaceSecs = Math.max(60, Math.round(3600 / speedKmh));

    const ms = paceToMs(newPaceSecs);
    setPaceMinutes(ms.minutes);
    setPaceSeconds(ms.seconds);

    if (calcTarget === 'time') {
      const calculatedSecs = calculateTime(distanceKm, newPaceSecs);
      const hms = secondsToHms(calculatedSecs);
      setHours(hms.hours);
      setMinutes(hms.minutes);
      setSeconds(hms.seconds);
    } else if (calcTarget === 'distance') {
      if (newPaceSecs > 0 && totalSeconds > 0) {
        const calculatedDist = calculateDistance(totalSeconds, newPaceSecs);
        setDistanceKm(Math.round(calculatedDist * 1000) / 1000);
      }
    }
  };

  // Reset to default
  const handleReset = () => {
    triggerHaptic(15);
    setDistanceKm(10);
    setSelectedPresetId('10k');
    setHours(0);
    setMinutes(50);
    setSeconds(0);
    setPaceMinutes(5);
    setPaceSeconds(0);
  };

  // Save to Logbook
  const handleSave = () => {
    triggerHaptic(15);
    const presetName = selectedPresetId !== 'custom'
      ? (distancePresets.find(p => p.id === selectedPresetId)?.name || `${displayDistance.toFixed(2)} ${displayDistanceUnit}`)
      : `${displayDistance.toFixed(2)} ${displayDistanceUnit}`;
    const suffix = t(language, 'uiRun');

    onSaveToLogbook({
      distanceKm,
      totalSeconds,
      paceSecondsPerKm: paceSecsPerKm,
      speedKmh: currentSpeedKmh,
      title: `${presetName} ${suffix}`,
    });
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2500);
  };

  // Share calculation with Deep-linking state
  const handleShare = async () => {
    triggerHaptic(10);
    const stateObj = {
      distanceKm,
      paceSecsPerKm,
      totalSeconds,
      calcTarget,
      distanceUnit,
    };
    const base64State = btoa(encodeURIComponent(JSON.stringify(stateObj)));
    const shareUrl = `${window.location.origin}${window.location.pathname}?calc=${base64State}`;

    const text = tf(language, 'shareRunText', {
      distance: displayDistance.toFixed(2),
      distanceUnit: displayDistanceUnit,
      time: formatTime(totalSeconds, true),
      pace: formatPace(displayPaceSecs),
      paceUnit: displayPaceUnit,
      speed: displaySpeed.toFixed(1),
      speedUnit: displaySpeedUnit,
    });

    if (navigator.share) {
      try {
        await navigator.share({
          title: t(language, 'uiPaceflowRunningPaceCalculation'),
          text: `${text}\n${shareUrl}`,
          url: shareUrl,
        });
      } catch {
        navigator.clipboard.writeText(`${text}\n${shareUrl}`);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2500);
      }
    } else {
      navigator.clipboard.writeText(`${text}\n${shareUrl}`);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  // Quick pace presets
  const quickPacesKm = [
    { label: '3:30', m: 3, s: 30 },
    { label: '4:00', m: 4, s: 0 },
    { label: '4:30', m: 4, s: 30 },
    { label: '5:00', m: 5, s: 0 },
    { label: '5:30', m: 5, s: 30 },
    { label: '6:00', m: 6, s: 0 },
    { label: '6:30', m: 6, s: 30 },
  ];

  const quickPacesMiles = [
    { label: '6:00', m: 6, s: 0 },
    { label: '6:30', m: 6, s: 30 },
    { label: '7:00', m: 7, s: 0 },
    { label: '7:30', m: 7, s: 30 },
    { label: '8:00', m: 8, s: 0 },
    { label: '8:30', m: 8, s: 30 },
    { label: '9:00', m: 9, s: 0 },
    { label: '10:00', m: 10, s: 0 },
  ];

  // Presets list tailored to unit and language
  const distancePresets = isMiles
    ? [
        { id: '1m', name: '1M', shortName: '1M', distanceKm: 1.60934 },
        { id: '3m', name: '3M', shortName: '3M', distanceKm: 4.82803 },
        { id: '5k', name: '5K', shortName: '5K', distanceKm: 5 },
        { id: '5m', name: '5M', shortName: '5M', distanceKm: 8.04672 },
        { id: '10k', name: '10K', shortName: '10K', distanceKm: 10 },
        { id: '10m', name: '10M', shortName: '10M', distanceKm: 16.0934 },
        { id: '21k', name: 'HMar', shortName: 'HMar', distanceKm: 21.0975 },
        { id: '42k', name: 'Mar', shortName: 'Mar', distanceKm: 42.195 },
      ]
    : [
        { id: '1k', name: '1K', shortName: '1K', distanceKm: 1 },
        { id: '5k', name: '5K', shortName: '5K', distanceKm: 5 },
        { id: '10k', name: '10K', shortName: '10K', distanceKm: 10 },
        { id: '15k', name: '15K', shortName: '15K', distanceKm: 15 },
        { id: '10m', name: '10M', shortName: '10M', distanceKm: 16.0934 },
        { id: '21k', name: 'HMar', shortName: 'HMar', distanceKm: 21.0975 },
        { id: '30k', name: '30K', shortName: '30K', distanceKm: 30 },
        { id: '42k', name: 'Mar', shortName: 'Mar', distanceKm: 42.195 },
        { id: '50k', name: '50K', shortName: '50K', distanceKm: 50 },
      ];

  return (
    <div className="space-y-4 sm:space-y-5 pb-6">
      {/* 1. Resultaatkaart (Actieve berekening, Output) */}
      <HeroResultCard
        activeCalculation={calcTarget}
        language={language}
        distanceKm={distanceKm}
        totalSeconds={totalSeconds}
        paceSecsPerKm={paceSecsPerKm}
        distanceUnit={distanceUnit}
        paceOrSpeedMode={paceOrSpeedMode}
        displayDistance={displayDistance}
        displayDistanceUnit={displayDistanceUnit}
        displayPaceSecs={displayPaceSecs}
        displayPaceUnit={displayPaceUnit}
        displaySpeed={displaySpeed}
        displaySpeedUnit={displaySpeedUnit}
      />

      {/* 2. Invoerkaarten (Input) - Oppervlakte is ALTIJD neutraal bg-slate-50 border-slate-100 */}
      {/* Afstand-invoerkaart (zichtbaar als actieve berekening 'time' of 'pace' is) */}
      {calcTarget !== 'distance' && (
        <DistanceInputCard
          language={language}
          distanceKm={distanceKm}
          isMiles={isMiles}
          distancePresets={distancePresets}
          distWholeKm={distWholeKm}
          distMeters={distMeters}
          distWholeMiles={distWholeMiles}
          distFeet={distFeet}
          onDistanceChange={handleDistanceChange}
          adjustWholeKm={adjustWholeKm}
          adjustMeters={adjustMeters}
          adjustWholeMiles={adjustWholeMiles}
          adjustFeet={adjustFeet}
          setExactFeet={setExactFeet}
          triggerHaptic={triggerHaptic}
        />
      )}

      {/* Tijd-invoerkaart (zichtbaar als actieve berekening 'pace' of 'distance' is) */}
      {calcTarget !== 'time' && (
        <TimeInputCard
          language={language}
          hours={hours}
          minutes={minutes}
          seconds={seconds}
          onTimeChange={handleTimeChange}
          triggerHaptic={triggerHaptic}
        />
      )}

      {/* Pace-invoerkaart (zichtbaar als actieve berekening 'time' of 'distance' is) */}
      {calcTarget !== 'pace' && (
        <PaceInputCard
          language={language}
          isMiles={isMiles}
          paceOrSpeedMode={paceOrSpeedMode}
          displaySpeed={displaySpeed}
          displaySpeedUnit={displaySpeedUnit}
          displayPaceUnit={displayPaceUnit}
          paceMinutes={paceMinutes}
          paceSeconds={paceSeconds}
          paceMileMs={paceMileMs}
          quickPacesKm={quickPacesKm}
          quickPacesMiles={quickPacesMiles}
          onPaceChangeKm={handlePaceChangeKm}
          onPaceChangeMiles={handlePaceChangeMiles}
          onSpeedChange={handleSpeedChange}
          triggerHaptic={triggerHaptic}
        />
      )}

      {/* 3. Primary Actions: Save & Share */}
      <div className="flex items-center gap-2.5 pt-2">
        <button
          id="btn-bottom-save-log"
          onClick={handleSave}
          className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl font-bold text-sm sm:text-base active:scale-98 transition-all cursor-pointer whitespace-nowrap ${
            savedSuccess
              ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 shadow-2xs'
              : 'bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-800 dark:text-slate-100 shadow-2xs'
          }`}
        >
          {savedSuccess ? (
            <>
              <Check className="w-4 h-4 stroke-[2.5]" />
              <span className="whitespace-nowrap">{t(language, 'saveSuccessLogbook')}</span>
            </>
          ) : (
            <>
              <BookmarkPlus className="w-4 h-4 stroke-[2.5] text-slate-600 dark:text-slate-400" />
              <span className="whitespace-nowrap">{t(language, 'saveToLogbook')}</span>
            </>
          )}
        </button>

        <button
          id="btn-bottom-share"
          onClick={handleShare}
          aria-label={t(language, 'shareCalculation')}
          className="h-12 w-12 sm:h-13 sm:w-13 rounded-2xl flex items-center justify-center bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 transition-all text-slate-700 dark:text-slate-200 cursor-pointer shadow-2xs shrink-0"
        >
          {copiedLink ? <Check className="w-5 h-5 text-emerald-500" /> : <Share2 className="w-5 h-5" />}
        </button>
      </div>

      {/* Voorspellingen & trainingszones */}
      <button
        id="btn-open-performance"
        onClick={onOpenPerformance}
        className="w-full flex items-center gap-4 text-left rounded-3xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-98 transition-all duration-150 p-4 cursor-pointer"
      >
        <span className="w-11 h-11 shrink-0 rounded-2xl bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 flex items-center justify-center">
          <Gauge className="w-5 h-5 text-[#7C3AED] dark:text-[#A78BFA]" />
        </span>
        <span className="min-w-0">
          <span className="block text-base font-bold text-slate-900 dark:text-white">{t(language, 'perfOpenTitle')}</span>
          <span className="block text-sm text-slate-600 dark:text-slate-400">{t(language, 'perfOpenDesc')}</span>
        </span>
      </button>

      {/* 4. Reset Action */}
      <div className="flex justify-center pt-1">
        <button
          id="btn-calc-reset"
          onClick={handleReset}
          className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 py-2 px-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>{t(language, 'resetDefaultsDesc')}</span>
        </button>
      </div>
    </div>
  );
}
